import { createStore } from './core.js';
import { createRestateApi } from './react.js';
import { registerStore } from './registry.js';

const defaultSerializer = Object.freeze({
    serialize(value) {
        const serialized = JSON.stringify(value);
        if (serialized === undefined) {
            throw new TypeError('The current Restate value is not JSON-serializable.');
        }
        return serialized;
    },
    deserialize(value) {
        return JSON.parse(value);
    },
});

function validateStorage(storage) {
    for (const method of ['getItem', 'setItem', 'removeItem']) {
        if (typeof storage?.[method] !== 'function') {
            throw new TypeError(`The storage adapter must implement ${method}().`);
        }
    }
    return storage;
}

function createCodec(options) {
    const serializer = options.serializer ?? defaultSerializer;
    if (
        typeof serializer.serialize !== 'function' ||
        typeof serializer.deserialize !== 'function'
    ) {
        throw new TypeError('serializer must implement serialize() and deserialize().');
    }

    const currentVersion = options.version ?? 1;
    if (!Number.isInteger(currentVersion) || currentVersion < 1) {
        throw new TypeError('version must be a positive integer.');
    }

    return {
        encode(value) {
            const data = serializer.serialize(value);
            if (typeof data !== 'string') {
                throw new TypeError('serializer.serialize() must return a string.');
            }
            return JSON.stringify({ __restate: 1, version: currentVersion, data });
        },
        decode(serialized) {
            const record = JSON.parse(serialized);
            let storedVersion = 0;
            let value;

            if (
                record &&
                record.__restate === 1 &&
                typeof record.data === 'string'
            ) {
                storedVersion = Number.isInteger(record.version) ? record.version : 0;
                value = serializer.deserialize(record.data);
            } else {
                value = serializer.deserialize(serialized);
            }

            if (storedVersion !== currentVersion && typeof options.migrate === 'function') {
                value = options.migrate(value, storedVersion, currentVersion);
            }
            return { value, storedVersion };
        },
    };
}

export function createPersistedRestate(initialValue, options, defaultStorageFactory) {
    if (!options || typeof options !== 'object') {
        throw new TypeError('persistedRestate requires an options object with a storage key.');
    }
    if (typeof options.key !== 'string' || options.key.trim().length === 0) {
        throw new TypeError('persistedRestate requires a non-empty key.');
    }

    const logicalKey = options.key.trim();
    const namespace = options.namespace ?? '@ryujaewan/restate';
    const storageKey = namespace ? `${namespace}:${logicalKey}` : logicalKey;
    const storage = validateStorage(
        options.storage ?? defaultStorageFactory(options.storageOptions)
    );
    const codec = createCodec(options);
    const store = createStore(initialValue, options.updater);
    const api = createRestateApi(store, 'persistent');

    let ready = false;
    let initializationPromise;
    let operationChain = Promise.resolve();
    let currentOperation = null;
    let lastError = null;
    let lastSavedAt = null;
    let lastRestoredAt = null;

    function storageIsAvailable() {
        return typeof storage.isAvailable === 'function' ? storage.isAvailable() : true;
    }

    function emit(type, details = {}) {
        store.emit(type, { key: logicalKey, storageKey, store: api, ...details });
    }

    function reportError(error, operation) {
        lastError = error;
        try {
            emit('error', { error, operation });
        } catch {
            // Preserve the operation error when an error observer also throws.
        }
    }

    function enqueue(operation, task) {
        const promise = operationChain
            .catch(() => undefined)
            .then(async () => {
                if (store.isDestroyed()) {
                    throw new Error(`Restate store "${api.id}" has been destroyed.`);
                }
                currentOperation = operation;
                try {
                    return await task();
                } catch (error) {
                    reportError(error, operation);
                    throw error;
                } finally {
                    currentOperation = null;
                }
            });
        operationChain = promise;
        return promise;
    }

    async function readSavedValue() {
        const serialized = await storage.getItem(storageKey);
        if (serialized == null) return { found: false };
        if (typeof serialized !== 'string') {
            throw new TypeError('The storage adapter returned a non-string value.');
        }
        return { found: true, ...codec.decode(serialized) };
    }

    function initialize() {
        if (initializationPromise) return initializationPromise;

        const startingRevision = store.getRevision();
        initializationPromise = enqueue('initialize', async () => {
            emit('initialize');
            if (!storageIsAvailable()) {
                ready = true;
                emit('ready', { available: false, restored: false });
                return store.get();
            }

            const saved = await readSavedValue();
            let restored = false;
            let skipped = false;
            if (saved.found) {
                if (store.getRevision() === startingRevision) {
                    store.replace(saved.value, 'restore', {
                        automatic: true,
                        storedVersion: saved.storedVersion,
                    });
                    lastRestoredAt = Date.now();
                    restored = true;
                } else {
                    skipped = true;
                }
            }
            ready = true;
            emit('ready', { available: true, restored, skipped });
            return store.get();
        }).catch((error) => {
            ready = true;
            emit('ready', { available: storageIsAvailable(), restored: false, error });
            return store.get();
        });

        return initializationPromise;
    }

    function save() {
        const value = store.get();
        let serialized;
        try {
            serialized = codec.encode(value);
        } catch (error) {
            reportError(error, 'save');
            return Promise.reject(error);
        }

        return enqueue('save', async () => {
            if (!storageIsAvailable()) {
                throw new Error('Persistent storage is unavailable in this environment.');
            }
            await storage.setItem(storageKey, serialized);
            lastSavedAt = Date.now();
            emit('save', { savedValue: value });
            return value;
        });
    }

    function restore() {
        return enqueue('restore', async () => {
            if (!storageIsAvailable()) {
                throw new Error('Persistent storage is unavailable in this environment.');
            }
            const saved = await readSavedValue();
            const value = saved.found ? saved.value : store.getInitial();
            store.replace(value, 'restore', {
                automatic: false,
                found: saved.found,
                storedVersion: saved.storedVersion,
            });
            lastRestoredAt = Date.now();
            return store.get();
        });
    }

    function hasSaved() {
        return enqueue('hasSaved', async () => {
            if (!storageIsAvailable()) return false;
            return (await storage.getItem(storageKey)) != null;
        });
    }

    function clearStorage() {
        return enqueue('clearStorage', async () => {
            if (!storageIsAvailable()) return false;
            await storage.removeItem(storageKey);
            emit('clearStorage');
            return true;
        });
    }

    function resetAll() {
        const value = api.reset();
        return clearStorage().then(() => value);
    }

    Object.assign(api, {
        key: logicalKey,
        storageKey,
        storage,
        initialize,
        save,
        restore,
        hasSaved,
        clearStorage,
        remove: clearStorage,
        resetAll,
        status() {
            return Object.freeze({
                ready,
                operation: currentOperation,
                available: storageIsAvailable(),
                error: lastError,
                lastSavedAt,
                lastRestoredAt,
            });
        },
    });

    api.ready = initialize();
    return registerStore(api);
}
